import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCanvasItemBounds,
  getCanvasWarnings,
  getProjectionBlendZones,
  snapCanvasCursorToItemCorner,
  snapCanvasItem,
} from '../canvas';
import { drawCanvasDocument, drawProjectionBlendZoneOverlay } from '../canvasRenderer';
import type { CanvasDocument, CanvasItemBounds } from '../types';

interface CanvasPreviewProps {
  canvas: CanvasDocument;
  selectedIds: string[];
  updateCanvas: (updater: (current: CanvasDocument) => CanvasDocument) => void;
  onSelect: (id: string | null, additive?: boolean) => void;
  onItemMoveStart: () => void;
  onItemMoveEnd: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
}

interface Point {
  x: number;
  y: number;
}

interface CursorPoint extends Point {
  snapped: boolean;
}

interface Interaction {
  pointerId: number;
  kind: 'item' | 'pan';
  startPoint: Point;
  startPan: Point;
  itemId?: string;
  itemOrigin?: Point;
}

interface SnapGuides {
  x: number | null;
  y: number | null;
}

export function CanvasPreview({
  canvas,
  selectedIds,
  updateCanvas,
  onSelect,
  onItemMoveStart,
  onItemMoveEnd,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
}: CanvasPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const interactionRef = useRef<Interaction | null>(null);
  const renderIdRef = useRef(0);
  const [viewport, setViewport] = useState({ width: 1, height: 1 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const [cursor, setCursor] = useState<CursorPoint | null>(null);
  const [guides, setGuides] = useState<SnapGuides>({ x: null, y: null });
  const warnings = useMemo(() => getCanvasWarnings(canvas), [canvas]);
  const blendZones = useMemo(() => getProjectionBlendZones(canvas), [canvas]);
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const fitScale = useMemo(() => {
    const availableWidth = Math.max(1, viewport.width - 56);
    const availableHeight = Math.max(1, viewport.height - 56);
    return Math.min(
      availableWidth / canvas.resolution.width,
      availableHeight / canvas.resolution.height,
    );
  }, [canvas.resolution, viewport]);
  const renderedScale = Math.max(0.0001, fitScale * zoom);
  const origin = useMemo(
    () => ({
      x: viewport.width / 2 - (canvas.resolution.width * renderedScale) / 2 + pan.x,
      y: viewport.height / 2 - (canvas.resolution.height * renderedScale) / 2 + pan.y,
    }),
    [canvas.resolution, pan, renderedScale, viewport],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setViewport({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const visibleCanvas = canvasRef.current;
    if (!visibleCanvas) return;
    const renderId = ++renderIdRef.current;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pixelWidth = Math.max(1, Math.round(viewport.width * dpr));
    const pixelHeight = Math.max(1, Math.round(viewport.height * dpr));
    visibleCanvas.width = pixelWidth;
    visibleCanvas.height = pixelHeight;
    visibleCanvas.style.width = `${viewport.width}px`;
    visibleCanvas.style.height = `${viewport.height}px`;

    const renderCanvas = document.createElement('canvas');
    renderCanvas.width = pixelWidth;
    renderCanvas.height = pixelHeight;
    const context = renderCanvas.getContext('2d', { alpha: true });
    if (!context) return;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.translate(origin.x, origin.y);
    context.scale(renderedScale, renderedScale);

    void drawCanvasDocument(context, canvas, {
      clear: false,
      previewProjectionBlends: true,
    }).then(() => {
      drawEditorOverlays(
        context,
        canvas,
        selectedIdSet,
        warnings,
        blendZones,
        guides,
        renderedScale,
      );
      if (renderId !== renderIdRef.current) return;
      const visibleContext = visibleCanvas.getContext('2d', { alpha: true });
      if (!visibleContext) return;
      visibleContext.setTransform(1, 0, 0, 1, 0, 0);
      visibleContext.clearRect(0, 0, pixelWidth, pixelHeight);
      visibleContext.drawImage(renderCanvas, 0, 0);
    });
  }, [blendZones, canvas, guides, origin, renderedScale, selectedIdSet, viewport, warnings]);

  const localPoint = (event: React.PointerEvent | React.WheelEvent): Point => {
    const bounds = containerRef.current!.getBoundingClientRect();
    return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
  };

  const toCanvasPoint = useCallback(
    (point: Point): Point => ({
      x: (point.x - origin.x) / renderedScale,
      y: (point.y - origin.y) / renderedScale,
    }),
    [origin, renderedScale],
  );

  const zoomAt = useCallback(
    (factor: number, point: Point) => {
      const nextZoom = clamp(zoom * factor, 0.05, 80);
      if (nextZoom === zoom) return;
      const center = { x: viewport.width / 2, y: viewport.height / 2 };
      const ratio = nextZoom / zoom;
      setPan({
        x: point.x - center.x - (point.x - center.x - pan.x) * ratio,
        y: point.y - center.y - (point.y - center.y - pan.y) * ratio,
      });
      setZoom(nextZoom);
    },
    [pan, viewport, zoom],
  );

  const moveSelectedByKeyboard = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      selectedIdSet.size === 0 ||
      !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)
    ) {
      return;
    }
    event.preventDefault();
    const amount = event.shiftKey ? 10 : 1;
    const delta = {
      x: event.key === 'ArrowLeft' ? -amount : event.key === 'ArrowRight' ? amount : 0,
      y: event.key === 'ArrowUp' ? -amount : event.key === 'ArrowDown' ? amount : 0,
    };
    updateCanvas((current) => ({
      ...current,
      items: current.items.map((item) =>
        selectedIdSet.has(item.id) ? { ...item, x: item.x + delta.x, y: item.y + delta.y } : item,
      ),
    }));
  };

  return (
    <section className="preview-shell canvas-preview-shell" aria-label="Canvas preview">
      <div className="preview-toolbar">
        <div className="preview-status">
          <strong>
            {canvas.resolution.width} × {canvas.resolution.height}
          </strong>
          <span>
            {canvas.items.length} {canvas.canvasType === 'led' ? 'LED screen' : 'projection'}
            {canvas.items.length === 1 ? '' : 's'}
          </span>
          {(blendZones.length > 0 ||
            warnings.overlapIds.size > 0 ||
            warnings.outsideIds.size > 0) && (
            <span className="canvas-status-warning">
              {warnings.outsideIds.size > 0 || canvas.canvasType === 'led' ? '⚠' : '↔'}{' '}
              {canvas.canvasType === 'projection'
                ? `${blendZones.length} blend zone${blendZones.length === 1 ? '' : 's'}`
                : `${warnings.overlapIds.size} overlapping`}{' '}
              · {warnings.outsideIds.size} outside
            </span>
          )}
        </div>
        <div className="preview-actions">
          <div className="history-actions history-actions--viewport" aria-label="Canvas history">
            <button
              type="button"
              className="history-button"
              disabled={!canUndo}
              title="Undo Canvas change (Ctrl/Cmd+Z)"
              onClick={onUndo}
            >
              <span aria-hidden="true">↶</span>
              <span className="sr-only">Undo Canvas change</span>
            </button>
            <button
              type="button"
              className="history-button"
              disabled={!canRedo}
              title="Redo Canvas change (Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y)"
              onClick={onRedo}
            >
              <span aria-hidden="true">↷</span>
              <span className="sr-only">Redo Canvas change</span>
            </button>
          </div>
          <button
            type="button"
            className="tool-button"
            onClick={() => zoomAt(1.25, { x: viewport.width / 2, y: viewport.height / 2 })}
          >
            +<span className="sr-only">Zoom in</span>
          </button>
          <button
            type="button"
            className="tool-button"
            onClick={() => zoomAt(0.8, { x: viewport.width / 2, y: viewport.height / 2 })}
          >
            −<span className="sr-only">Zoom out</span>
          </button>
          <button
            type="button"
            className="tool-button tool-button--wide"
            onClick={() => {
              setZoom(1);
              setPan({ x: 0, y: 0 });
            }}
          >
            Fit
          </button>
          <button
            type="button"
            className="tool-button tool-button--wide"
            onClick={() => {
              setZoom(clamp(1 / fitScale, 0.05, 80));
              setPan({ x: 0, y: 0 });
            }}
          >
            100%
          </button>
        </div>
      </div>
      <div
        ref={containerRef}
        className="preview-stage canvas-stage"
        tabIndex={0}
        onKeyDown={moveSelectedByKeyboard}
        onWheel={(event) => {
          event.preventDefault();
          zoomAt(event.deltaY < 0 ? 1.12 : 0.89, localPoint(event));
        }}
        onPointerDown={(event) => {
          event.currentTarget.focus();
          event.currentTarget.setPointerCapture(event.pointerId);
          const point = localPoint(event);
          const canvasPoint = toCanvasPoint(point);
          const hit = [...canvas.items]
            .reverse()
            .find((item) => pointInBounds(canvasPoint, getCanvasItemBounds(item)));
          if (hit) {
            onItemMoveStart();
            onSelect(
              hit.id,
              canvas.canvasType === 'projection' &&
                (event.ctrlKey || event.metaKey || event.shiftKey),
            );
            interactionRef.current = {
              pointerId: event.pointerId,
              kind: 'item',
              startPoint: canvasPoint,
              startPan: pan,
              itemId: hit.id,
              itemOrigin: { x: hit.x, y: hit.y },
            };
          } else {
            onSelect(null);
            interactionRef.current = {
              pointerId: event.pointerId,
              kind: 'pan',
              startPoint: point,
              startPan: pan,
            };
          }
        }}
        onPointerMove={(event) => {
          const point = localPoint(event);
          const canvasPoint = toCanvasPoint(point);
          setCursor(
            snapCanvasCursorToItemCorner(canvas, canvasPoint.x, canvasPoint.y, 10 / renderedScale),
          );
          const interaction = interactionRef.current;
          if (!interaction || interaction.pointerId !== event.pointerId) return;
          if (interaction.kind === 'pan') {
            setPan({
              x: interaction.startPan.x + point.x - interaction.startPoint.x,
              y: interaction.startPan.y + point.y - interaction.startPoint.y,
            });
            return;
          }
          if (!interaction.itemId || !interaction.itemOrigin) return;
          const rawX = interaction.itemOrigin.x + canvasPoint.x - interaction.startPoint.x;
          const rawY = interaction.itemOrigin.y + canvasPoint.y - interaction.startPoint.y;
          const snap = event.altKey
            ? { x: Math.round(rawX), y: Math.round(rawY), guideX: null, guideY: null }
            : snapCanvasItem(canvas, interaction.itemId, rawX, rawY, 8 / renderedScale);
          setGuides({ x: snap.guideX, y: snap.guideY });
          updateCanvas((current) => ({
            ...current,
            items: current.items.map((item) =>
              item.id === interaction.itemId ? { ...item, x: snap.x, y: snap.y } : item,
            ),
          }));
        }}
        onPointerUp={(event) => {
          if (interactionRef.current?.pointerId !== event.pointerId) return;
          if (interactionRef.current.kind === 'item') onItemMoveEnd();
          interactionRef.current = null;
          setGuides({ x: null, y: null });
        }}
        onPointerCancel={() => {
          if (interactionRef.current?.kind === 'item') onItemMoveEnd();
          interactionRef.current = null;
          setGuides({ x: null, y: null });
        }}
        onPointerLeave={() => setCursor(null)}
      >
        <canvas ref={canvasRef} />
        {cursor?.snapped && (
          <span
            className="canvas-corner-snap-marker"
            style={{
              left: origin.x + cursor.x * renderedScale,
              top: origin.y + cursor.y * renderedScale,
            }}
            aria-hidden="true"
          />
        )}
        {cursor && (
          <output className={`cursor-readout${cursor.snapped ? ' is-snapped' : ''}`}>
            X {cursor.x} · Y {cursor.y}
          </output>
        )}
        <div className="zoom-readout">{Math.round(renderedScale * 100)}%</div>
      </div>
    </section>
  );
}

function drawEditorOverlays(
  context: CanvasRenderingContext2D,
  canvas: CanvasDocument,
  selectedIds: ReadonlySet<string>,
  warnings: ReturnType<typeof getCanvasWarnings>,
  blendZones: ReturnType<typeof getProjectionBlendZones>,
  guides: SnapGuides,
  scale: number,
): void {
  context.save();
  if (canvas.background === 'transparent') {
    context.lineWidth = Math.max(1, 2 / scale);
    context.setLineDash([]);
    context.strokeStyle = '#8ea6b8';
    context.strokeRect(0, 0, canvas.resolution.width, canvas.resolution.height);
  }
  drawProjectionBlendZoneOverlay(context, blendZones, scale);
  context.lineWidth = Math.max(1, 2 / scale);
  context.setLineDash([Math.max(2, 8 / scale), Math.max(2, 5 / scale)]);
  for (const item of canvas.items) {
    const selected = selectedIds.has(item.id);
    const warning =
      warnings.outsideIds.has(item.id) ||
      (canvas.canvasType === 'led' && warnings.overlapIds.has(item.id));
    if (!selected && !warning) continue;
    const bounds = getCanvasItemBounds(item);
    context.strokeStyle = warning ? '#f5b942' : '#24c8db';
    context.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
  }
  context.setLineDash([]);
  context.lineWidth = Math.max(1, 1.5 / scale);
  context.strokeStyle = '#6ef1ff';
  if (guides.x !== null) {
    context.beginPath();
    context.moveTo(guides.x, 0);
    context.lineTo(guides.x, canvas.resolution.height);
    context.stroke();
  }
  if (guides.y !== null) {
    context.beginPath();
    context.moveTo(0, guides.y);
    context.lineTo(canvas.resolution.width, guides.y);
    context.stroke();
  }
  context.restore();
}

function pointInBounds(point: Point, bounds: CanvasItemBounds): boolean {
  return (
    point.x >= bounds.x &&
    point.y >= bounds.y &&
    point.x < bounds.x + bounds.width &&
    point.y < bounds.y + bounds.height
  );
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
